import React from "react"
import { css } from "react-emotion"

// Import React Table
import ReactTable from "react-table"
import "react-table/react-table.css"
import { withTheme } from "emotion-theming"
import Text from "ui/components/Text"

import checkboxHOC from "react-table/lib/hoc/selectTable"

const CheckboxTable = checkboxHOC(ReactTable)

class Table extends React.Component {
    constructor(props) {
        super(props)
        const { columns, data } = props
        this.state = {
            data,
            columns,
            selection: [],
            selectAll: false,
        }
    }

    componentWillReceiveProps({ columns, data }) {
        this.setState({ columns, data })
    }

    toggleSelection = (key, shift, row) => {
        /*
      Implementation of how to manage the selection state is up to the developer.
      This implementation uses an array stored in the component state.
      Other implementations could use object keys, a Javascript Set, or Redux... etc.
    */
        // start off with the existing state
        let selection = [...this.state.selection]
        const keyIndex = selection.indexOf(key)
        // check to see if the key exists
        if (keyIndex >= 0) {
            // it does exist so we will remove it using destructing
            selection = [...selection.slice(0, keyIndex), ...selection.slice(keyIndex + 1)]
        } else {
            // it does not exist so add it
            selection.push(key)
        }
        // update the state
        this.setState({ selection })
    }

    toggleAll = () => {
        /*
      'toggleAll' is a tricky concept with any filterable table
      do you just select ALL the records that are in your data?
      OR
      do you only select ALL the records that are in the current filtered data?

      The latter makes more sense because 'selection' is a visual thing for the user.
      This is especially true if you are going to implement a set of external functions
      that act on the selected information (you would not want to DELETE the wrong thing!).

      So, to that end, access to the internals of ReactTable are required to get what is
      currently visible in the table (either on the current page or any other page).

      The HOC provides a method call 'getWrappedInstance' to get a ref to the wrapped
      ReactTable and then get the internal state and the 'sortedData'.
      That can then be iterrated to get all the currently visible records and set
      the selection state.
    */
        const selectAll = this.state.selectAll ? false : true
        const selection = []
        if (selectAll) {
            // we need to get at the internals of ReactTable
            const wrappedInstance = this.checkboxTable.getWrappedInstance()
            // the 'sortedData' property contains the currently accessible records based on the filter and sort
            const currentRecords = wrappedInstance.getResolvedState().sortedData
            // we just push all the IDs onto the selection array
            currentRecords.forEach(item => {
                selection.push(item._original._sid)
            })
        }
        this.setState({ selectAll, selection })
    }

    isSelected = key => {
        /*
      Instead of passing our external selection state we provide an 'isSelected'
      callback and detect the selection state ourselves. This allows any implementation
      for selection (either an array, object keys, or even a Javascript Set object).
    */
        return this.state.selection.includes(key)
    }

    logSelection = () => {
        console.log("selection:", this.state.selection)
    }

    render() {
        const { toggleSelection, toggleAll, isSelected } = this
        const { data, columns, selectAll } = this.state
        const defaultPageSize = 20
        const tableStyle = css`
            font-family: ${this.props.theme.fontFamily};
            color: ${this.props.theme.fontColor};
            font-size: ${this.props.theme.fontSize};
            border: none;

            .rt-table {
                overflow: unset;
                overflow-y: visible !important;
                overflow-x: scroll;
                padding-bottom: 250px;
                margin-bottom: -250px;

         

            }

            .pagination-bottom {
                z-index: 2;
            }

            .rt-thead.-header {
                box-shadow: none;
                border-bottom: ${this.props.theme.dividerThickness + 1}px solid
                    ${this.props.theme.dividerColor};
                color: ${this.props.theme.columnHeadingColor};
                font-size: ${this.props.theme.columnHeadingSize};
                font-weight: ${this.props.theme.columnHeadingWeight};
                text-transform: capitalize;
            }

            .rt-thead .rt-tr {
                text-align: left !important;
            }

            .rt-tr .hover {
                visibility: hidden;
            }

            .rt-tr:hover .hover {
                visibility: visible;
            }

            .rt-tbody {
                overflow: unset;
                overflow-y: visible !important;
                overflow-x: auto;
                overflow: visible
            }

            .rt-tbody .rt-td {
                border-right: 0px;
                padding: ${this.props.theme.small};
                overflow: visible;
                position: relative;


                & > p, & > a {
                    width: 100%;
                    overflow: unset;
                    overflow-y: visible;
                    overflow-x: auto;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                & p {
                    width: 100%;
                    overflow: unset;
                    overflow-y: visible;
                    overflow-x: auto;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                & i {
                    width: unset;
                }

                & .table-no-ellipsis p {
                    width: auto;

                }


            }
            .rt-thead .rt-th {
                border-right: 0px;
                padding: ${this.props.theme.small};
            }
            .rt-tbody .rt-tr-group {
                border-bottom: px solid
                    ${this.props.theme.dividerColor};
            }


            .-pagination {
                box-shadow: none;
                border: none;
                margin-top: 20px;
                ${data.length <= defaultPageSize ? "display:none;" : ""}

    }

            .-pagination.- btn {
    background: none;
    color: ${this.props.theme.linkTextColor};
    text - align: inherit;

}
            .-pagination.- previous {
    text - align: right;

}
            .-pagination.- next {
    text - align: left;

}
            .-pagination.- btn: hover {
    background: none!important;
    color: ${this.props.theme.linkTextColor} !important;
}
            }

            .-pagination.- btn[disabled] {
    opacity: .1;
    cursor: default ;
}

            .-pageSizeOptions {
    display: none;
}
            .-pageInfo{
    color: grey;
    font - size: 0.8em
}
`

        const checkboxProps = {
            selectAll,
            isSelected,
            toggleSelection,
            toggleAll,
            selectType: "checkbox",
            getTrProps: (s, r) => {
                // someone asked for an example of a background color change
                // here it is...
                const selected = r && this.isSelected(r.original._sid)
                return {
                    className: selected
                        ? css`
                              background-color: ${this.props.theme.rowHighlightColor};
                          `
                        : "",
                }
            },
        }

        const TableComponent = this.props.checkboxes ? CheckboxTable : ReactTable

        return (
            <React.Fragment>
                {this.props.data.length !== 0 ? (
                    <TableComponent
                        ref={r => (this.checkboxTable = r)}
                        data={data}
                        columns={columns}
                        minRows={0}
                        defaultPageSize={defaultPageSize}
                        className={tableStyle}
                        defaultSorted={this.props.defaultSorted}
                        {...checkboxProps}
                    />
                ) : (
                    <Text size="fontM" padding="none" margin="none">
                        No {this.props.dataLabel || "records"} have been created yet
                    </Text>
                )}
            </React.Fragment>
        )
    }
}

export default withTheme(Table)



// WEBPACK FOOTER //
// ./src/ui/components/Table.js